import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileDown, FileSpreadsheet, Mail, Settings2, Tags, Pencil, Lock, Layers, Scale, ListChecks } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';
import { VisaCostFilters, filtersToParams } from './VisaCostFilters';
import { VisaCostEditDialog } from './VisaCostEditDialog';
import { VisaCostManageDialog } from './VisaCostManageDialog';
import { EmailReportDialog, EmailSettingsDialog } from './VisaEmailDialogs';
import { BulkApplyDialog } from './BulkApplyDialog';
import { ActualsDialog } from './ActualsDialog';
import { RenewalTracker } from './RenewalTracker';
import { ActualVsBudget } from './ActualVsBudget';

const VIEWS = [['costs', 'Costs', Tags], ['tracker', 'Renewal Tracker', ListChecks], ['avb', 'Actual vs Budget', Scale]];

const money = (v) => `AED ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_TONE = { valid: 'bg-emerald-50 text-emerald-700 border-emerald-200', due_soon: 'bg-amber-50 text-amber-800 border-amber-200',
  expired: 'bg-red-50 text-red-700 border-red-200', no_date: 'bg-gray-50 text-gray-500 border-gray-200', cancelled: 'bg-slate-100 text-slate-600 border-slate-300' };
const EMPTY = { period: '', month: '', year: '', start: '', end: '', employee_id: '', department: '', cost_centre_id: '', status: '' };

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-white px-4 py-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{value}</p>
  </div>
);

export default function VisaCostsTab() {
  const [meta, setMeta] = useState({ can_edit: null, descriptions: [], cost_centres: [], departments: [] });
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], summary: { grand_total: 0, employees: 0, with_costs: 0, by_cost_centre: {} }, period_label: '' });
  const [allEmps, setAllEmps] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [dlg, setDlg] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get('sub') || 'costs');
  const [actualRow, setActualRow] = useState(null);
  const params = useMemo(() => filtersToParams(f), [f]);

  const loadMeta = useCallback(() => api.get('/api/visa-costs/meta').then((r) => setMeta(r.data)).catch(() => {}), []);
  const loadRows = useCallback(() => {
    setLoading(true);
    return api.get('/api/visa-costs/rows', { params })
      .then((r) => setData(r.data))
      .catch((e) => toast({ title: 'Could not load visa costs', description: e?.response?.data?.detail || e.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { loadMeta(); api.get('/api/visa-costs/rows').then((r) => setAllEmps(r.data.rows)).catch(() => {}); }, [loadMeta]);
  useEffect(() => { if (view === 'costs') loadRows(); }, [loadRows, view]);

  const download = async (format) => {
    try {
      const r = await api.get('/api/visa-costs/report', { params: { ...params, format }, responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="(.+)"/)?.[1] || `visa_costs.${format}`;
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement('a'), { href: url, download: name }).click();
      URL.revokeObjectURL(url);
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  const startTracker = async (r) => {
    try { await api.post(`/api/visa-costs/records/${r.employee_id}/tracker/start`); toast({ title: 'Tracker started', description: `${r.name} added to the Renewal Tracker.` }); loadRows(); }
    catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const { rows, summary } = data;
  return (
    <div className="space-y-4" data-testid="visa-costs-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900 mr-2">Visa Costs & Renewals</h2>
        {meta.can_edit === false && <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" />View only</Badge>}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => download('pdf')} data-testid="vc-download-pdf"><FileDown className="h-4 w-4 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={() => download('xlsx')} data-testid="vc-download-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          {meta.can_edit && <Button size="sm" onClick={() => setDlg('email')} data-testid="vc-email-report-btn"><Mail className="h-4 w-4 mr-1" />Email to Finance</Button>}
          {meta.can_edit && <Button size="sm" variant="outline" onClick={() => setDlg('bulk')} data-testid="vc-bulk-btn"><Layers className="h-4 w-4 mr-1" />Bulk template</Button>}
          <Button size="sm" variant="outline" onClick={() => setDlg('manage')} data-testid="vc-manage-btn"><Tags className="h-4 w-4 mr-1" />Descriptions & Cost Centres</Button>
          <Button size="sm" variant="outline" onClick={() => setDlg('settings')} data-testid="vc-email-settings-btn"><Settings2 className="h-4 w-4 mr-1" />Email Settings</Button>
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="vc-subviews">
        {VIEWS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`vc-view-${k}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${view === k ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-800'}`}>
            <Icon className="h-4 w-4" />{l}
          </button>
        ))}
      </div>

      {view === 'tracker' && <RenewalTracker canEdit={!!meta.can_edit} />}
      {view === 'avb' && <ActualVsBudget departments={meta.departments} />}
      {view === 'costs' && (<>
      <VisaCostFilters f={f} setF={setF} meta={meta} employees={allEmps} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Renewal period" value={data.period_label || '—'} testId="vc-stat-period" />
        <Stat label="Employees" value={`${summary.employees} (${summary.with_costs} costed)`} testId="vc-stat-employees" />
        <Stat label="Total visa cost" value={money(summary.grand_total)} testId="vc-stat-total" />
        <div className="rounded-lg border bg-white px-4 py-3" data-testid="vc-stat-centres">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">By cost centre</p>
          <div className="text-xs space-y-0.5 mt-1 max-h-16 overflow-y-auto">
            {Object.entries(summary.by_cost_centre).map(([k, v]) => <p key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><b>{money(v)}</b></p>)}
            {!Object.keys(summary.by_cost_centre).length && <p className="text-gray-400">—</p>}
          </div>
        </div>
      </div>

      <div className={`border rounded-lg bg-white overflow-x-auto transition-opacity ${loading && rows.length ? 'opacity-60' : ''}`}>
        <table className="w-full text-sm" data-testid="vc-table">
          <thead className="bg-slate-50 text-[11px] uppercase text-gray-500">
            <tr>{['Employee', 'Department', 'Visa Co.', 'Renewal date', 'Status', 'Cost breakdown', 'Total', ''].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.employee_id} className="hover:bg-slate-50/60 align-top" data-testid={`vc-row-${r.employee_code}`}>
                <td className="px-3 py-2"><p className="font-medium text-gray-900">{r.name}</p><p className="text-xs text-gray-500">{r.employee_code}</p></td>
                <td className="px-3 py-2 text-gray-700">{r.department}</td>
                <td className="px-3 py-2 text-gray-700">{r.visa_company || '—'}</td>
                <td className="px-3 py-2">{r.renewal_date || '—'}{r.renewal_override && <p className="text-[10px] text-indigo-600">override (expiry {r.visa_expiry || '—'})</p>}</td>
                <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded border ${STATUS_TONE[r.status]}`}>{r.status_label}</span></td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {r.items.length ? r.items.map((i) => (
                    <p key={i.id}>{i.description}: <b>{Number(i.amount).toFixed(2)}</b>{i.cost_centre_code && <span className="text-gray-400"> · {i.cost_centre_code}</span>}</p>
                  )) : <span className="text-gray-400">{r.legacy_visa_cost ? `No breakdown (Staff Cost has ${money(r.legacy_visa_cost)})` : 'No costs entered'}</span>}
                </td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{money(r.total)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-col items-start">
                    {meta.can_edit && <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditRow(r)} data-testid={`vc-edit-${r.employee_code}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>}
                    <Button size="sm" variant="ghost" className="h-7" onClick={() => setActualRow(r)} data-testid={`vc-actuals-${r.employee_code}`}><Scale className="h-3.5 w-3.5 mr-1" />Actuals{r.history_count ? ` (${r.history_count})` : ''}</Button>
                    {meta.can_edit && !r.tracker && r.renewal_date && <Button size="sm" variant="ghost" className="h-7" onClick={() => startTracker(r)} data-testid={`vc-track-${r.employee_code}`}><ListChecks className="h-3.5 w-3.5 mr-1" />Track</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {loading && !rows.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400" data-testid="vc-loading">Loading visa costs…</td></tr>}
            {!loading && !rows.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">No employees match these filters.</td></tr>}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="bg-amber-50 font-semibold"><td colSpan={6} className="px-3 py-2 text-right">Grand total</td><td className="px-3 py-2" data-testid="vc-grand-total">{money(summary.grand_total)}</td><td /></tr></tfoot>
          )}
        </table>
      </div>

      </>)}

      <VisaCostEditDialog row={editRow} meta={meta} onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); loadRows(); }} />
      <VisaCostManageDialog open={dlg === 'manage'} onClose={() => setDlg('')} meta={meta} onChanged={() => { loadMeta(); loadRows(); }} />
      <EmailReportDialog open={dlg === 'email'} onClose={() => setDlg('')} params={params} periodLabel={data.period_label} />
      <BulkApplyDialog open={dlg === 'bulk'} onClose={() => setDlg('')} meta={meta} onDone={() => { setDlg(''); loadRows(); }} />
      <ActualsDialog row={actualRow} canEdit={!!meta.can_edit} onClose={() => setActualRow(null)} onSaved={(done) => { if (done) setActualRow(null); loadRows(); }} />
      <EmailSettingsDialog open={dlg === 'settings'} onClose={() => setDlg('')} canEdit={meta.can_edit} />
    </div>
  );
}
